import React, { useEffect, useState } from 'react';
import Card from '@material-ui/core/Card';
import SearchIcon from '@material-ui/icons/Search';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@material-ui/core';
import { ErrorOutline } from '@material-ui/icons';
import { useIntl } from 'react-intl';
import moment from 'moment';

import { CollaboratorPages } from '../CollaboratorUpload.types';

import {
  UpcomingEventStyle,
  Cards,
  Title,
  Topic,
  SubTopic,
  SubTopicFail,
  Uploads,
  UploadFail,
  SubTopic1,
} from './UpcomingEvent.style';
import { UpcomingEventProps, EventVideoStatus } from './UpcomingEvent.types';
import uploadIcon from 'assets/icons/upload-icon.svg';
import { Loader } from 'shared/components/loader/Loader';
import { EventInformation, EventVideosDetails } from 'app/avUserRoot/AVUserRoot.types';
import { EventState } from 'api/actions/avUser/avUser.types';
import { Button as SharedButton } from 'shared/components/button/Button';

const INLINE_VIDEO_THRESHOLD = 5;

const sortByStartTimeDesc = (events: EventInformation[]) =>
  [...events].sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

const openVideoPreview = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const UpcomingEvent = ({ setCollaboratorPage, avUserDetails, setEventDetails }: UpcomingEventProps) => {
  const classes = UpcomingEventStyle();
  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState(false);
  const [searchTerms, setSearchTerms] = useState('');
  const [filteredAVUserDetails, setFilteredAVUserDetails] = useState<EventInformation[] | null>(null);
  const [selectedEventVideos, setSelectedEventVideos] = useState<EventVideosDetails[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Single consolidated effect — avoids the triple re-render from 3 separate effects all watching avUserDetails
  useEffect(() => {
    if (!avUserDetails) {
      setLoading(true);

      return;
    }
    setLoading(false);

    if (searchTerms.length) {
      const lower = searchTerms.toLowerCase();
      const filtered = avUserDetails.filter(
        d => d.name?.toLowerCase().includes(lower) || d.institution.name.toLowerCase().includes(lower),
      );

      setFilteredAVUserDetails(sortByStartTimeDesc(filtered));
    } else {
      setFilteredAVUserDetails(sortByStartTimeDesc(avUserDetails));
    }
  }, [avUserDetails, searchTerms]);

  return (
    <Cards>
      <Card elevation={0}>
        <Title>
          {formatMessage({
            id: 'av_user.upcoming_events.title',
          })}
        </Title>
        <div className={classes.searchBarContainer}>
          <TextField
            value={searchTerms}
            variant="outlined"
            placeholder={formatMessage({
              id: 'av_user.upcoming_events.search_placeholder',
            })}
            className={classes.searchBar}
            onChange={({ target: { value } }) => setSearchTerms(value)}
            InputProps={{
              startAdornment: <SearchIcon className={classes.searchIcon} />,
            }}
          />
        </div>

        {filteredAVUserDetails && !!filteredAVUserDetails.length ? (
          filteredAVUserDetails.map(data => {
            const uploadedEventVideos =
              data.eventVideos
                ?.filter(eventVideo => !!eventVideo.url)
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) ?? [];

            return (
              <Card elevation={1} className={classes.availableCard} key={data.id}>
                <div className={classes.subTopic1}>
                  <SubTopic1>
                    {`${data.institution.name} (${moment(data.startTime).format('DD/MMM/YYYY')} at ${moment(
                      data.startTime,
                    ).format(' hh:mm a')})`}
                  </SubTopic1>
                </div>
                <div className={classes.available}>
                  <div className={classes.availableImage}>
                    {data.state === EventVideoStatus.Published ? (
                      <img src={uploadIcon} alt="Event" className={classes.image} />
                    ) : (
                      <div className={data.state === EventState.WaitingForAssets ? classes.upload : classes.imageDiv}>
                        <img
                          src={uploadIcon}
                          alt=""
                          className={data.state === EventState.Cancelled ? classes.failedIcon : classes.uploadIcon}
                        />
                      </div>
                    )}
                  </div>
                  <div className={classes.content}>
                    <div className={classes.side}>
                      <div className={classes.topic}>
                        <Topic>{data.name}</Topic>
                      </div>
                    </div>

                    <div className={classes.subTopic}>
                      {data.state && data.state === EventState.Cancelled ? (
                        <SubTopicFail>
                          {formatMessage({
                            id: 'av_user.upcoming_events.failed',
                          })}
                        </SubTopicFail>
                      ) : (
                        <SubTopic>
                          {formatMessage({
                            id: 'av_user.upcoming_events.upload_file',
                          })}
                        </SubTopic>
                      )}
                    </div>
                  </div>
                  <div className={classes.state}>
                    {data.state && data.state === EventState.Cancelled ? (
                      <UploadFail>
                        {formatMessage({
                          id: 'av_user.upcoming_events.upload_failed',
                        })}
                      </UploadFail>
                    ) : (
                      <>
                        {!!uploadedEventVideos.length && (
                          <Button
                            type="button"
                            variant="outlined"
                            className={classes.viewVideoActionButton}
                            startIcon={<PlayCircleOutlineIcon className={classes.viewVideoActionIcon} />}
                            onClick={() => setSelectedEventVideos(uploadedEventVideos)}
                          >
                            View Video
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outlined"
                          className={classes.uploadActionButton}
                          startIcon={<CloudUploadIcon className={classes.uploadActionIcon} />}
                          onClick={() => {
                            setCollaboratorPage(CollaboratorPages.uploadMedia);
                            setEventDetails(data);
                          }}
                        >
                          {formatMessage({
                            id: 'av_user.upcoming_events.upload',
                          })}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        ) : (
          <Card elevation={0} className={classes.root}>
            {loading ? (
              <Loader />
            ) : (
              <Grid container className={classes.grid}>
                <Card elevation={1} className={classes.uploads}>
                  <div className={classes.errorDiv}>
                    <ErrorOutline className={classes.ErrorLogo} />
                    <Typography className={classes.uploadContent}>
                      {formatMessage({
                        id: 'av_user.upcoming_events.no_event',
                      })}
                    </Typography>
                  </div>
                </Card>
              </Grid>
            )}
          </Card>
        )}

        <Dialog
          open={!!selectedEventVideos}
          onClose={() => setSelectedEventVideos(null)}
          fullWidth
          maxWidth="md"
          aria-labelledby="uploaded-event-videos"
        >
          <DialogTitle id="uploaded-event-videos">
            {formatMessage({
              id: 'av_user.upload_media.title',
            })}
          </DialogTitle>
          <DialogContent dividers>
            {selectedEventVideos && selectedEventVideos.length <= INLINE_VIDEO_THRESHOLD ? (
              // Inline video player for small sets — no extra click needed
              selectedEventVideos.map(video => (
                <div key={video.id} className={classes.inlineVideoItem}>
                  <Typography className={classes.inlineVideoName}>{video.name}</Typography>
                  <Typography variant="caption" className={classes.inlineVideoMeta}>
                    {`${formatMessage({
                      id: 'av_user.upload_media.created_at',
                    })} ${moment(video.createdAt).format('DD/MMM/YYYY hh:mm a')}`}
                  </Typography>
                  <Typography variant="caption" className={classes.inlineVideoMeta}>
                    {`${formatMessage({
                      id: 'av_user.upload_media.video_size',
                    })} ${video.data.size}`}
                  </Typography>
                  <div className={classes.inlineVideoWrapper}>
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <video
                      key={video.url}
                      controls
                      controlsList="nodownload"
                      disablePictureInPicture
                      playsInline
                      className={classes.inlineVideo}
                    >
                      <source src={video.url} type="video/mp4" />
                    </video>
                  </div>
                </div>
              ))
            ) : (
              // List with external link for larger sets
              <List disablePadding>
                {selectedEventVideos?.map(video => (
                  <ListItem key={video.id} divider>
                    <ListItemText
                      primary={video.name}
                      secondary={`${formatMessage({
                        id: 'av_user.upload_media.created_at',
                      })} ${moment(video.createdAt).format('DD/MMM/YYYY hh:mm a')}`}
                    />
                    <Button
                      variant="text"
                      type="button"
                      onClick={() => openVideoPreview(video.url)}
                      disabled={!video.url}
                    >
                      {formatMessage({
                        id: 'av_user.upload_media.play_video',
                      })}
                    </Button>
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <SharedButton variant="text" type="button" onClick={() => setSelectedEventVideos(null)}>
              {formatMessage({
                id: 'dialog.cancel_label',
              })}
            </SharedButton>
          </DialogActions>
        </Dialog>
      </Card>
    </Cards>
  );
};
